import { CustomerType } from "@/apps/credit-line/models/CreditLineDetails";

export type Country = "MX" | "CO";

export type Features = {
  [key: string]: {
    supportedCountries: Array<Country>;
    supportedCustomers?: Array<CustomerType>;
    fullyDisabled: boolean;
    // eslint-disable-next-line  @typescript-eslint/no-explicit-any
    featureValues?: Record<string, any>;
  };
};

/**
 * List here features you want to keep under control by client side code.
 * In the future, this data could be replaced by an API call if needed.
 */
export const FEATURES: Features = {
  bank_transfer_withdrawals: {
    supportedCountries: ["MX"],
    fullyDisabled: false,
  },
  kyc: {
    supportedCountries: ["MX", "CO"],
    supportedCustomers: [
      CustomerType.PJM_PRIOR_BORROWER,
      CustomerType.JOINT_LOAN_LINE,
    ],
    fullyDisabled: false,
  },
  aml_survey: {
    supportedCountries: ["MX", "CO"],
    fullyDisabled: false,
    featureValues: {
      questions: {
        MX: ["incomeSource", "loanPurpose", "monthlyIncome"],
        CO: [
          "incomeSource",
          "monthlyIncome",
          "politicallyExpodedPerson",
          "politicallyExpodedFamilyMember",
        ],
      },
    },
  },
  chatbot: {
    supportedCountries: ["MX"],
    fullyDisabled: false,
  },
  payjoy_card: {
    supportedCountries: ["MX"],
    fullyDisabled: false,
  },
  whatsapp: {
    supportedCountries: ["MX", "CO"],
    fullyDisabled: false,
    featureValues: {
      number: {
        MX: "+525575028669",
        CO: "+573022830945",
        PE: "+51978662235",
      },
    },
  },
  withdrawal_instructions: {
    supportedCountries: ["MX", "CO"],
    fullyDisabled: false,
    featureValues: {
      summary_fields: {
        MX: ["reference", "contract", "concept"],
        CO: ["verificationCode"],
      },
    },
  },
  offer_fields: {
    supportedCountries: ["MX", "CO"],
    fullyDisabled: false,
    featureValues: {
      fields: {
        MX: [],
        CO: ["availabilityFeeRate", "insuranceFeeRate"],
      },
    },
  },
  statement_fields: {
    supportedCountries: ["MX", "CO"],
    fullyDisabled: false,
    featureValues: {
      fields: {
        MX: [],
        CO: ["availabilityFeeRate", "insuranceFeeRate"],
      },
    },
  },
  pin_flow: {
    supportedCountries: ["MX", "CO"],
    fullyDisabled: false,
    featureValues: {
      pin_fields: {
        MX: ["bank"],
        CO: ["directPinChange"],
      },
    },
  },
  shipmentFields: {
    supportedCountries: ["MX", "CO"],
    fullyDisabled: false,
    featureValues: {
      shipment_fields: {
        MX: ["street", "neighborhood", "postalCode", "city", "notes"],
        CO: ["street", "neighborhood", "city", "notes", "apartment"],
      },
    },
  },
  phone_number: {
    supportedCountries: ["MX", "CO"],
    fullyDisabled: false,
    featureValues: {
      phone_fields: {
        MX: ["52"],
        CO: ["57"],
      },
    },
  },
  pb_credit_pull: {
    supportedCountries: ["MX", "CO"],
    fullyDisabled: false,
  },
  payjoy_card_faqs: {
    supportedCountries: ["MX", "CO"],
    fullyDisabled: false,
  },
  help_page_tac: {
    supportedCountries: ["MX"],
    fullyDisabled: false,
    featureValues: {
      url: {
        MX: "https://www.payjoy.com/mx/terminos-y-condiciones",
      },
    },
  },
  customer_charter: {
    supportedCountries: ["MX", "CO"],
    fullyDisabled: false,
    featureValues: {
      url: {
        MX: "https://live-upstream.payjoy.com/mx/sobre-nosotros/nuestro-compromiso",
        CO: "https://live-upstream.payjoy.com/co/sobre-nosotros/nuestro-compromiso",
      },
    },
  },
  identity_validation: {
    supportedCountries: ["MX", "CO"],
    fullyDisabled: false,
    featureValues: {
      validation_type: {
        MX: "CURP",
        CO: "CEDULA",
        PE: "CEDULA",
      },
      document_name: {
        MX: "INE",
        CO: "cédula",
        PE: "cédula",
      },
      validation_image: {
        MX: "img/credit-line/credit-card/kycMexicoCard.png",
        CO: "img/credit-line/credit-card/idValidationColombia.svg",
        PE: "img/credit-line/credit-card/kycPeruCard.svg",
      },
    },
  },
};

/**
 * This service is in charge of implementing feature filters and "questions".
 * Make sure all methods have a return value type of "Features" or boolean
 */
export class FeatureControlService {
  getAllFeatures(): Features {
    return FEATURES;
  }

  getFeaturesByCountry(country: Country): Features {
    const filtered: Features = {};
    const featureNames = Object.keys(FEATURES);
    featureNames.forEach((featureName) => {
      const feature = FEATURES[featureName];
      if (feature.supportedCountries.includes(country)) {
        filtered[featureName] = feature;
      }
    });
    return filtered;
  }

  isFeatureEnabled(
    featureName: string,
    country: Country,
    customerType?: CustomerType
  ): boolean {
    const feature = FEATURES[featureName];
    if (!feature) {
      return false;
    }

    if (!country) {
      return false;
    }

    if (feature.fullyDisabled) {
      return false;
    }

    if (!feature.supportedCountries.includes(country)) {
      return false;
    }

    if (customerType && !feature.supportedCustomers?.includes(customerType)) {
      return false;
    }

    return true;
  }

  getFeatureValue(
    featureName: string,
    country: string,
    desiredValue: string
    // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  ): any {
    const feature = FEATURES[featureName];
    if (feature.featureValues) {
      return feature.featureValues[desiredValue][country];
    } else {
      return "";
    }
  }
}

export const AvailableFeatures = {
  BANK_TRANSFER: "bank_transfer_withdrawals",
  KYC: "kyc",
  AML_SURVEY: "aml_survey",
  CHATBOT: "chatbot",
  PAYJOY_CARD: "payjoy_card",
  WHATSAPP: "whatsapp",
  WITHDRAWAL_INSTRUCTIONS: "withdrawal_instructions",
  OFFER_FIELDS: "offer_fields",
  STATEMENT_FIELDS: "statement_fields",
  PIN_FLOW: "pin_flow",
  PB_CREDIT_PULL: "pb_credit_pull",
  PJ_CARD_FAQS: "payjoy_card_faqs",
  PHONE_NUMBER: "phone_number",
  HELP_PAGE_TAC: "help_page_tac",
  CUSTOMER_CHARTER: "customer_charter",
  SHIPMENT_FIELS: "shipmentFields",
  IDENTITY_VALIDATION: "identity_validation",
};
